import { useContext, useEffect, useState } from 'react'
import { AuthContext } from 'contexts/AuthContext'
import { Wrapper } from 'components'

import { useNavigate, useParams } from 'react-router-dom'
import {
  Button,
  Card,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  toast,
} from 'keep-react'
import { ArrowLeft, DownloadSimple, Rocket } from 'phosphor-react'
import { InvoiceListResponse, InvoiceType } from 'api/types/invoice'
import { invoiceService } from 'api/services/invoice'
import { RouteNamesAuth } from 'routes/enum'
import { formatCurrency } from 'utils/currency'
import { docService } from 'api/services/doc'
import { downloadPDF } from 'utils/downloadPdf'
import { format } from 'date-fns'
import { AppContext } from 'contexts/AppContext'

export const Invoice = () => {
  const { receiptNo } = useParams()

  const { currentBroker } = useContext(AuthContext)
  const { setIsCallingApi } = useContext(AppContext)
  const brokerNo = currentBroker?.BrokerNo ? currentBroker?.BrokerNo : 0
  const [data, setData] = useState<InvoiceType | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const navigate = useNavigate()

  const getInvoiceDetail = async () => {
    try {
      const response = (await invoiceService.getInvoiceList({
        brokerNo,
        receiptNo: receiptNo || '',
        page: 0,
      })) as InvoiceListResponse

      if (response.Invoices.length === 0) {
        toast.error('Recibo não encontrado')
        setIsLoading(false)
        navigate(RouteNamesAuth.INVOICES)
        return
      } else {
        setIsLoading(false)
        setData(response.Invoices[0])
      }
    } catch (error) {
      toast.error('Erro a pedir o recibo')
      navigate(RouteNamesAuth.INVOICES)
    }
  }

  useEffect(() => {
    getInvoiceDetail()
  }, [brokerNo])

  // download receipt document
  const getReceipt = async () => {
    setIsCallingApi(true)
    try {
      if (!data) return
      const response = await docService.getDocumentList(
        Number(data['Apólice']),
        0,
        'I',
        receiptNo,
      )
      if (!response.Documents || response.Documents.length === 0) {
        toast.error('Nenhum documento encontrado')
        return
      }
      downloadPDF({
        base64Data: response.Documents[0].Document,
        filename: `Aviso de Recibo - ${data['Apólice']}.pdf`,
      })
    } catch (error) {
      console.error(error)
    } finally {
      setIsCallingApi(false)
    }
  }

  // download invoice document
  const getInvoice = async () => {
    setIsCallingApi(true)
    try {
      if (!data) return
      const response = await docService.getDocumentList(
        Number(data['Apólice']),
        0,
        'R',
        receiptNo,
      )
      if (!response.Documents || response.Documents.length === 0) {
        toast.error('Nenhum documento encontrado')
        return
      }
      downloadPDF({
        base64Data: response.Documents[0].Document,
        filename: `Factura Recibo -${data['Apólice']}.pdf`,
      })
    } catch (error) {
      console.error(error)
    } finally {
      setIsCallingApi(false)
    }
  }

  return (
    <div className="w-full flex-1 py-12 overflow-auto">
      <Wrapper>
        <Button
          variant="outline"
          color="secondary"
          className="mb-6"
          onClick={() => navigate(RouteNamesAuth.INVOICES)}
        >
          <ArrowLeft size={16} /> Recibos
        </Button>
        <h2 className="uppercase text-sm font-medium text-metal-900">
          Consulte o seu recibo
        </h2>
        <h1 className="pb-10 text-xxxl font-medium text-primary-600">Recibo</h1>
        <div className="grid grid-col-1 lg:grid-cols-[3fr_1fr] gap-4 w-full">
          <Card className="p-4 w-full max-w-full">
            {isLoading ? (
              <Spinner color="primary" className="mx-auto" />
            ) : !data ? (
              <div className="text-heading-6 text-metal-400">
                Recibo indisponível
              </div>
            ) : (
              <div>
                <div className="flex items-start gap-2">
                  <div className="flex items-center flex-col gap-1 pt-3 -translate-y-1.5">
                    <Rocket />
                  </div>
                  <h2 className="text-body-1 uppercase pb-10">
                    {data['Produto']}
                  </h2>
                </div>
                <div className="pb-10">
                  <Table>
                    <TableBody>
                      {Object.entries(data).map(([key, value], index) => {
                        if (key === 'ModifiedDate' || key === 'Pago por')
                          return null

                        if (key === 'Pago' && value === 'Pago') {
                          const v = Object.entries(data).find(
                            ([k, _]) => k === 'Pago por',
                          )
                          value = `Pago por ${v ? v[1].toLowerCase() : ''}`
                        } else {
                          if (key === 'Pago' && value !== 'Pago') {
                            value = 'Por liquidar'
                          }
                        }

                        return (
                          <TableRow key={`row-${index}`}>
                            <TableHead>
                              {key === 'Pago' ? 'Estado' : key}
                            </TableHead>
                            <TableCell>
                              {key === 'Comissão' ||
                              key === 'Prémio Total' ||
                              key === 'Prémio Comercial'
                                ? formatCurrency(value)
                                : key === 'Data de Emissão' ||
                                    key === 'Data de Cobrança'
                                  ? format(new Date(value), 'yyyy/MM/dd')
                                  : key === 'Pago'
                                    ? value
                                    : key === 'Forma de Pagamento' &&
                                        value === 'By Invoice'
                                      ? 'Multibanco/Transf. Bancária'
                                      : key === 'Forma de Pagamento' &&
                                          value === 'Direct Debit'
                                        ? 'Débito Direto'
                                        : String(value)}
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </Card>
          <div>
            <Card className="p-4 w-full max-w-full">
              <nav className="flex flex-col items-start justify-start gap-4">
                <Button
                  color="secondary"
                  variant="softBg"
                  onClick={() => getReceipt()}
                  className="gap-2 w-full"
                >
                  <DownloadSimple size={16} />
                  Aviso de Recibo
                </Button>
                <Button
                  color="secondary"
                  variant="softBg"
                  onClick={() => getInvoice()}
                  className="gap-2 w-full"
                >
                  <DownloadSimple size={16} />
                  Factura Recibo
                </Button>
              </nav>
            </Card>
          </div>
        </div>
      </Wrapper>
    </div>
  )
}
